import { useTranslation } from 'next-i18next';
import { useMemo, useRef } from 'react';
import styled from 'styled-components';
import Flex, { ColumnCenter, ColumnFlex } from '@/components/Flex';
import ImageWrapper from '@/components/ImageWrapper';
import VideoBackground from '@/components/VideoBackground';
import Card from '@/components/surface/Card';
import LandingTitle from '@/components/text/LandingTitle';
import StyledText from '@/components/text/StyledText';
import useDeviceSize from '@/hooks/mobile/useDeviceSize';
import useHover from '@/hooks/useHover';
import SectionHomePage from './SectionHomePage';

const UsageCard = styled(Card)<{ isActive?: boolean }>`
  ${({ theme, isActive }) => theme.opacity[isActive ? 100 : 60]}
  ${({ theme, isActive }) => theme.shadow[isActive ? 'card' : 'unset']}
  ${({ theme }) => theme.transition.default}
`;

function UsageContent({
  isHovered: isContainerHovered,
  index,
  header,
  body,
  bgVideo,
}: {
  isHovered?: boolean;
  index: number;
  header?: React.ReactNode;
  body?: React.ReactNode;
  bgVideo?: string;
}) {
  // ref
  const ref = useRef<HTMLDivElement>(null);

  // hover
  const isHovered = useHover(ref);

  // memo
  const isActive = useMemo(
    () => (!isContainerHovered && index === 0) || isHovered,
    [index, isContainerHovered, isHovered]
  );

  return (
    <UsageCard
      $padding="md"
      $rounded="xl"
      $opacity={60}
      $shadow="unset"
      isActive={isActive}
      ref={ref}
    >
      <ColumnFlex
        $width={200}
        $height="100%"
        $justify="space-between"
        $gap="md"
      >
        <ColumnCenter $gap="xs">
          <StyledText $fontSize="md" $fontWeight="bold">
            {index + 1}. {header}
          </StyledText>
          <StyledText
            $fontSize="xs"
            $fontWeight="regular"
            $color="secondary"
            $textAlign="center"
            $whiteSpace="pre-wrap"
          >
            {body}
          </StyledText>
        </ColumnCenter>

        <ImageWrapper $rounded="xl" $width={200} $height={200}>
          <VideoBackground isPaused={!isActive} bgVideo={bgVideo} />
        </ImageWrapper>
      </ColumnFlex>
    </UsageCard>
  );
}

export default function UsageSection() {
  // device size
  const deviceSize = useDeviceSize();

  // translate
  const { t } = useTranslation('home', { keyPrefix: 'usage' });

  // ref
  const ref = useRef<HTMLDivElement>(null);

  // hover
  const isHovered = useHover(ref);

  // memo
  const contents = useMemo(
    () =>
      (
        t('contents', { returnObjects: true }) as {
          header?: string;
          body?: string;
        }[]
      ).map((content, contentIndex) => ({
        ...content,
        bgVideo: `/video/video-home-usage-${contentIndex + 1}`,
      })),
    [t]
  );

  return (
    <SectionHomePage>
      <ColumnCenter $gap="xl">
        <LandingTitle>{t('title')}</LandingTitle>
        <StyledText
          $fontSize="md"
          $fontWeight="regular"
          $color="secondary"
          $textAlign="center"
          $whiteSpace="pre-wrap"
        >
          {t('description')}
        </StyledText>
      </ColumnCenter>
      <Flex
        $direction={deviceSize === 'mobile' ? 'column' : undefined}
        $justify="center"
        $gap={
          deviceSize === 'mobile'
            ? 'xs'
            : deviceSize === 'tablet'
              ? 'xxs'
              : 'md'
        }
        ref={ref}
      >
        {contents.map((content, index) => (
          <UsageContent
            isHovered={isHovered}
            index={index}
            {...content}
            key={index}
          />
        ))}
      </Flex>
    </SectionHomePage>
  );
}
